<script setup>
import { computed, ref } from 'vue'
import { useLotteryStore } from '../stores/lottery.js'

const props = defineProps({
  lotteryId: {
    type: String,
    required: true,
  },
  selectedTickets: {
    type: Array,
    default: () => [],
  },
  ticketsPerBatch: {
    type: Number,
    default: 0,
  },
  ticketPrice: {
    type: [String, Number],
    default: null,
  },
})

const emit = defineEmits(['upload-success'])

const lotteryStore = useLotteryStore()
const file = ref(null)
const uploadError = ref('')
const uploadMessage = ref('')

const ticketPriceNumber = computed(() => {
  const parsed = Number(props.ticketPrice)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
})
const hasTicketPrice = computed(() => props.ticketPrice !== null && props.ticketPrice !== undefined && String(props.ticketPrice) !== '')

const submissionTotalValue = computed(() => {
  const soldCount = Array.isArray(props.selectedTickets) ? props.selectedTickets.length : 0
  return ticketPriceNumber.value * soldCount
})

const submissionTotalValueDisplay = computed(() => submissionTotalValue.value.toFixed(2))

const handleFileChange = (e) => {
  const files = e.target.files
  if (files && files.length > 0) {
    file.value = files[0]
  }
}

const handleSubmit = async () => {
  uploadError.value = ''
  uploadMessage.value = ''

  if (!file.value) {
    uploadError.value = 'Please select a file'
    return
  }

  if (!props.selectedTickets.length) {
    uploadError.value = 'Please select at least one sold ticket before upload.'
    return
  }

  if (props.ticketsPerBatch > 0 && props.selectedTickets.length > props.ticketsPerBatch) {
    uploadError.value = 'Selected tickets exceed tickets_per_batch limit.'
    return
  }

  const result = await lotteryStore.uploadTicketBatch(
    props.lotteryId,
    file.value,
    JSON.stringify(props.selectedTickets),
  )

  if (result.success) {
    uploadMessage.value = 'Upload successful!'
    file.value = null
    emit('upload-success')
  } else {
    uploadError.value = result.error
  }
}
</script>

<template>
  <form @submit.prevent="handleSubmit" class="upload-form">
    <div class="form-group">
      <label for="file-input">Select image file:</label>
      <input
        id="file-input"
        type="file"
        accept="image/*"
        @change="handleFileChange"
        required
      />
    </div>

    <button type="submit" :disabled="lotteryStore.loading">
      {{ lotteryStore.loading ? 'Uploading...' : 'Upload' }}
    </button>

    <p class="hint">
      Sold tickets selected: <strong>{{ selectedTickets.length }}</strong>
    </p>

    <p class="hint" v-if="hasTicketPrice">
      Submission value preview: <strong>{{ submissionTotalValueDisplay }}</strong>
    </p>

    <div v-if="uploadMessage" class="message success">
      {{ uploadMessage }}
    </div>

    <div v-if="uploadError" class="message error">
      {{ uploadError }}
    </div>
  </form>
</template>

<style scoped>
.upload-form {
  display: grid;
  gap: 1rem;
}

.form-group {
  display: grid;
  gap: 0.5rem;
}

label {
  color: #111827;
  font-weight: bold;
}

input[type='file'] {
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  color: #111827;
}

button {
  padding: 0.75rem;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
}

button:hover:not(:disabled) {
  background-color: #1976d2;
}

button:disabled {
  background-color: #ccc;
}

.message {
  padding: 0.75rem;
  border-radius: 4px;
}

.hint {
  margin: 0;
  color: #374151;
}

.message.success {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.message.error {
  background-color: #ffebee;
  color: #c62828;
}
</style>
